import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch, } from 'vue';
import { useKycStore } from '@/store/modules/kyc';
import { storeToRefs } from 'pinia';
import { getTransactionChannelType } from '@/utils/helpers';
import { KycTypes } from '@/types/kyc';
import router, { RoutePaths } from '@/router';
import { useAuthStore } from '@/store/modules/auth';
import useTransactionsStore from '@/store/modules/transactions';
import { usePaymentStore } from '@/store/modules/payment';
import { PaymentTypes } from '@/types/payment';
import { TransactTypes } from '@/types/transact';
import { MIXPANEL_EVENTS, trackEvent } from '@/utils/mixpanel';
var KycSteps = KycTypes.KycSteps;
var PaymentSteps = PaymentTypes.PaymentSteps;
var KycStatus = KycTypes.KycStatus;
export default defineComponent({
    name: 'VerificationStatus',
    computed: {
        KycTypes() {
            return KycTypes;
        },
    },
    props: {},
    components: {},
    setup() {
        const kycStore = useKycStore();
        const authStore = useAuthStore();
        const transactionStore = useTransactionsStore();
        const paymentStore = usePaymentStore();
        const { currentStep, kycStatus } = storeToRefs(kycStore);
        const { user, token, deviceId, partnerSessionId } = storeToRefs(authStore);
        const { userTransactions, selectedChannel, selectedCurrency, selectedFiatAmountWithoutFees, buyFlow, pollForBankDetails, txType, payment, } = storeToRefs(transactionStore);
        const { currentStep: paymentStep } = storeToRefs(paymentStore);
        const intervalId = ref(null);
        const directToBankInfo = ref(false);
        const getKycStatus = async () => {
            try {
                return await kycStore.GetKycStatus(partnerSessionId.value, deviceId.value, token.value);
            }
            catch (error) {
                console.error('Error fetching KYC status:', error);
                return kycStatus.value;
            }
        };
        onMounted(async () => {
            if (kycStatus.value === KycTypes.KycStatus.UNDER_REVIEW ||
                kycStatus.value === KycTypes.KycStatus.PENDING_COMPLIANCE) {
                await Promise.all([
                    getKycStatus(),
                    transactionStore.getUserTransactions(),
                ]);
                intervalId.value = window.setInterval(async () => {
                    const status = await getKycStatus();
                    if ([
                        KycStatus.BLOCKED,
                        KycStatus.REJECTED,
                        KycStatus.APPROVED,
                    ].includes(status)) {
                        clearInterval(Number(intervalId.value));
                        if (status === KycStatus.BLOCKED)
                            currentStep.value = KycSteps.BLOCKED;
                        // check current flow
                        switch (txType.value) {
                            case TransactTypes.TransactionType.BUY:
                                // if approved and user has an existing order, redirect to bank details
                                if (status === KycStatus.APPROVED &&
                                    userTransactions.value.length > 0) {
                                    buyFlow.value = true;
                                    selectedChannel.value = getTransactionChannelType(userTransactions.value[0]);
                                    selectedFiatAmountWithoutFees.value =
                                        userTransactions.value[0].convertedAmount;
                                    selectedCurrency.value = {
                                        currency: userTransactions.value[0].currency,
                                        editable: true,
                                    };
                                    pollForBankDetails.value = true;
                                    directToBankInfo.value = true;
                                }
                                break;
                            case TransactTypes.TransactionType.SELL:
                                // if approved user has a payment record, redirect to wallet details
                                if (status === KycStatus.APPROVED && payment.value !== null) {
                                    transactionStore.getMinipayOrWalletRoute();
                                    router.push(RoutePaths.Payment);
                                }
                                break;
                            case TransactTypes.TransactionType.KYC:
                                if ([KycStatus.BLOCKED, KycStatus.APPROVED].includes(status)) {
                                    window.location.href =
                                        'https://pfi.tbd.engineering/public/finish.html';
                                }
                                break;
                            default:
                        }
                    }
                }, 10000);
            }
            // adding this for users already approved
            if ([KycStatus.BLOCKED, KycStatus.APPROVED].includes(kycStatus.value) &&
                txType.value === TransactTypes.TransactionType.KYC) {
                window.location.href = 'https://pfi.tbd.engineering/public/finish.html';
            }
        });
        watch(directToBankInfo, newValue => {
            if (newValue) {
                paymentStep.value = PaymentSteps.PAYMENT_2;
                router.push('/payment');
            }
        });
        onBeforeUnmount(() => {
            clearInterval(Number(intervalId.value));
        });
        const kycSubmittedDate = computed(() => user.value && user.value.kycSubmittedAt
            ? new Date(user.value.kycSubmittedAt).toLocaleDateString('en-UK')
            : new Date().toLocaleDateString('en-UK'));
        const reviewCompletedDate = computed(() => user.value && user.value.kycReviewCompletedAt
            ? new Date(user.value.kycReviewCompletedAt).toLocaleDateString('en-UK')
            : new Date().toLocaleDateString('en-UK'));
        const kycDecisionDate = computed(() => user.value && user.value.kycApprovedAt
            ? new Date(user.value.kycApprovedAt).toLocaleDateString('en-UK')
            : new Date().toLocaleDateString('en-UK'));
        const backClick = async () => {
            await router.push('/');
        };
        const onKeyPress = () => {
            console.log();
        };
        const restartKycVerification = () => {
            trackEvent(MIXPANEL_EVENTS.KYC.RETRY_VERIFICATION, {
                screen: 'Verification Status Screen',
            });
            kycStore.resetStore();
            currentStep.value = KycTypes.KycSteps.KYC_1;
        };
        return {
            backClick,
            onKeyPress,
            kycStatus,
            kycSubmittedDate,
            restartKycVerification,
            reviewCompletedDate,
            kycDecisionDate,
        };
    },
});
